import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  BookingEvent,
  BookingEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/trade/booking_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { BookingCreatedDetail } from './BookingCreatedDetail';
import { BookingDeletedDetail } from './BookingDeletedDetail';
import { BookingStatusTransitionDetail } from './BookingStatusTransitionDetail';
import { BookingUpdatedDetail } from './BookingUpdatedDetail';

const bookingStatusEventLabels = {
  opened: 'Open',
  shipped: 'Shipped',
  completed: 'Completed',
  voided: 'Voided',
} as const;

type BookingEventDetailProps = {
  payload: string;
};

export const BookingEventDetail = ({ payload }: BookingEventDetailProps) => {
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): BookingEvent | null => {
    try {
      return fromJson(BookingEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title="Raw Payload (Parse Error)" />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <BookingCreatedDetail event={inner.value} />;
      case 'updated':
        return <BookingUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <BookingDeletedDetail event={inner.value} />;
      case 'opened':
      case 'shipped':
      case 'completed':
      case 'voided':
        return (
          <BookingStatusTransitionDetail
            booking={inner.value.booking}
            previousStatus={inner.value.previousStatus}
            newStatusLabel={bookingStatusEventLabels[inner.case]}
          />
        );
      case undefined:
        return <JsonViewer data={payload} title="Unknown Event Type" />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return 'Booking Created';
      case 'updated':
        return 'Booking Updated';
      case 'deleted':
        return 'Booking Deleted';
      case 'opened':
        return 'Booking Opened';
      case 'shipped':
        return 'Booking Shipped';
      case 'completed':
        return 'Booking Completed';
      case 'voided':
        return 'Booking Voided';
      case undefined:
        return 'Unknown Event';
    }
  };

  return (
    <Col className="@BookingEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
